<script setup lang="ts">
import {Org, OrgMember} from '@/types'
import {computed, h} from 'vue'
import {useStore} from 'vuex'

import {ColumnDef} from "@tanstack/vue-table"
import {ArrowUpDown} from "lucide-vue-next"
import {Button} from '@/components/ui/button'
import {Badge} from '@/components/ui/badge'
import {MemberAvatar} from '@/components/ui/avatar'
import {DataTable} from '@/components/ui/data-table'
import {can} from '@/composables/useCan'
import RoleSelect from '@/components/roles/RoleSelect.vue'
import MembersListDropdown from './MembersListDropdown.vue'

const props = defineProps<{
  org: Org,
  members: OrgMember[],
}>()

const store = useStore()
const canUpdate = computed(() => can('org_member', 'update', {orgId: props.org.organization_id}))

const ownerMemberId = computed(() => store.getters['access/ownerMemberId'])

const changeRole = (member: OrgMember, roleId: number) => store.dispatch('org/setMemberRole', {member, roleId})

const columns = computed<ColumnDef<OrgMember>[]>(() => [
  {
    accessorKey: 'display_name',
    header: ({column}) => {
      return h(Button, {
        variant: 'ghost',
        size: 'sm',
        onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
      }, () => ['Name', h(ArrowUpDown, {class: 'ml-1 h-3 w-3'})])
    },
    cell: ({row}) => {
      const member = row.original
      const avatar = h(MemberAvatar, {avatar_url: member.avatar_url, display_name: member.display_name, verified: member.verified})
      if (member.member_id !== ownerMemberId.value) return avatar
      return h('div', {class: 'flex items-center gap-2'}, [avatar, h(Badge, {variant: 'secondary', 'data-testid': 'owner-badge'}, () => 'Owner')])
    },
  },
  {
    accessorKey: 'email',
    header: ({column}) => {
      return h(Button, {
        variant: 'ghost',
        size: 'sm',
        onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
      }, () => ['Email', h(ArrowUpDown, {class: 'ml-1 h-3 w-3'})])
    },
    cell: ({row}) => h('div', {class: 'lowercase'}, row.getValue('email')),
  },
  {
    id: 'role',
    header: 'Role',
    cell: ({row}) => {
      const member = row.original
      if (!canUpdate.value) {
        return h('div', {class: 'text-gray-500'}, store.getters['access/role'](member.role_id)?.name ?? '')
      }
      return h(RoleSelect, {
        kind: 'org',
        modelValue: member.role_id,
        'onUpdate:modelValue': (roleId: number) => changeRole(member, roleId),
      })
    },
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => {
      const member = row.original
      return h('div', { class: 'relative' }, h(MembersListDropdown, { member }))
    },
  }
])
</script>

<template>
  <DataTable :columns="columns" :data="members" />
</template>
